import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Segment } from "@mui/icons-material";
import { Button } from "@mui/material";
import { useSnackbar } from "notistack";

import { LoadingButton } from "ui";

import { hydrateLayer, selectDraftConfigEntities, useUpdateLayerConfigMutation } from "fond/api";
import { LayerConfig, LayerConfigHydrated, SublayerConfigHydrated } from "fond/types/ProjectLayerConfig";
import { makeUuid } from "fond/utils";
import { generateColors } from "fond/utils/colors";
import { Modal } from "fond/widgets";

import ClassifyForm, { IClassifyFormData } from "./ClassifyForm";
import { generateStyleFromClassification, getDefaultStyleValues } from "./helper";

import { ContentWrapper } from "./Classify.styles";

// When using gradients, generate more colors to create smoother interpolation stops
// Each classification will get multiple color stops instead of just 2
export const STOPS_PER_CLASSIFICATION = 5;

interface IProps {
  layerConfig: LayerConfig;
  mapLayerConfigId: string;
  onClose(): void;
}

const Classification: React.FC<IProps> = ({ layerConfig, mapLayerConfigId, onClose }: IProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const draft = useSelector(selectDraftConfigEntities);
  const [updateLayerConfig] = useUpdateLayerConfigMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formValues, setFormValues] = useState<IClassifyFormData>({
    colorRamp: undefined,
    range: "equal_intervals",
    groupSize: 4,
    layerClassifications: [],
    gradientColors: false,
  });

  const colors = useMemo(() => {
    const count = formValues.layerClassifications.filter(({ FilterConfiguration }) => FilterConfiguration.Type !== "other").length;

    return generateColors(formValues.colorRamp, formValues.invertColorRamp, formValues.gradientColors ? count * STOPS_PER_CLASSIFICATION + 1 : count);
  }, [formValues.layerClassifications, formValues.colorRamp, formValues.invertColorRamp, formValues.gradientColors]);

  /**
   * Handles the cancellation of the classification by calling the
   * onClose callback.
   */
  const handleOnCancel = () => {
    onClose();
  };

  /**
   * Handles the creation of the new sublayers and styles
   */
  const handleOnSubmit = async () => {
    const attribute = layerConfig.Attributes?.find(({ ID }) => ID === formValues.attributeId);
    const newLayer = hydrateLayer(draft, layerConfig) as LayerConfigHydrated;

    // Clear existing styles at the layer level
    newLayer.Styles = [];

    // Create new Sublayers
    let colorIndex = 0;

    newLayer.Children = formValues.layerClassifications.map((layerClassification, index) => {
      const sublayerKey = makeUuid();

      // Get colors for this classification
      let classificationColors: string[];
      if (formValues.gradientColors && layerClassification.FilterConfiguration.Type !== "other") {
        // Get STOPS_PER_CLASSIFICATION + 1 colors for smooth interpolation
        const startIdx = colorIndex;
        const endIdx = colorIndex + STOPS_PER_CLASSIFICATION;
        classificationColors = colors.slice(startIdx, endIdx + 1);
        // Move to next color range (overlap by 1 for smooth transitions between classifications)
        colorIndex += STOPS_PER_CLASSIFICATION;
      } else {
        classificationColors = [colors[colorIndex] || colors[0]];
        if (layerClassification.FilterConfiguration.Type !== "other") {
          colorIndex += 1;
        }
      }

      return {
        ID: null,
        Type: "SUBLAYER",
        Label: layerClassification.Label,
        IsVisible: true,
        Hidden: false,
        ParentID: layerConfig.ID,
        Styles: [
          {
            ...generateStyleFromClassification({
              attributeId: attribute?.Name || "",
              layerClassification,
              colors: classificationColors,
            }),
            ...getDefaultStyleValues(formValues),
          },
        ],
        FilterConfiguration: layerClassification.FilterConfiguration,
        Position: index,
        Key: sublayerKey,
      } as SublayerConfigHydrated & { ID: null };
    });

    setIsSubmitting(true);
    try {
      await updateLayerConfig({ layerConfig: newLayer, mapLayerConfigId });
      onClose();
    } catch (error) {
      enqueueSnackbar("Failed to classify layer", { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      headerIcon={<Segment />}
      header="Classify layer"
      data-testid="classify-layer-modal"
      showClose
      showFullscreen
      variant="primary"
      DialogContentProps={{ style: { display: "flex", flexDirection: "column", minHeight: 0 } }}
      content={
        <ContentWrapper>
          <ClassifyForm colors={colors} initialValues={formValues} isSubmitting={isSubmitting} layerConfig={layerConfig} onChange={setFormValues} />
        </ContentWrapper>
      }
      actions={
        <>
          <Button data-testid="new-style-cancel-button" color="primary" onClick={handleOnCancel} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <LoadingButton
            data-testid="new-style-save-button"
            color="primary"
            type="submit"
            onClick={handleOnSubmit}
            disabled={formValues.layerClassifications.length === 0}
            loading={isSubmitting}
          >
            Apply
          </LoadingButton>
        </>
      }
    />
  );
};

export default Classification;
